import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formSchema = z.object({
  name: z.string().min(2, 'Namn måste vara minst 2 tecken'),
  phone: z.string().min(6, 'Ange ett giltigt telefonnummer'),
  email: z.string().email('Ange en giltig e-postadress'),
  property_type: z.string().min(1, 'Välj fastighetstyp'),
  message: z.string().optional(),
});

export const LeadForm = ({ id }) => {
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      property_type: '',
      message: '',
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/leads`, data);
      setSubmitStatus('success');
      reset();
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div
        id={id}
        className="bg-white p-8 md:p-12 border border-[#E2E8F0] text-center animate-fade-in"
        data-testid="form-success-message"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-2xl font-heading font-bold text-[#1F2937] mb-4">
          Tack för din förfrågan!
        </h3>
        <p className="text-[#64748B] mb-6">
          Vi har tagit emot dina uppgifter och återkommer inom 24 timmar med en offert 
          eller för att boka in en besiktning.
        </p>
        <button
          onClick={() => setSubmitStatus(null)}
          className="text-[#C0392B] font-semibold hover:underline"
          data-testid="form-reset-button"
        >
          Skicka en ny förfrågan
        </button>
      </div>
    );
  }

  return (
    <div
      id={id}
      className="bg-white p-8 md:p-12 border border-[#E2E8F0] relative z-10"
      data-testid="lead-form-container"
    >
      <div className="mb-8">
        <h3 className="text-2xl font-heading font-bold text-[#1F2937] mb-2">
          Få kostnadsfri offert
        </h3>
        <p className="text-[#64748B]">
          Fyll i formuläret så hör vi av oss inom 24 timmar.
        </p>
      </div>

      {submitStatus === 'error' && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 flex items-start gap-3" data-testid="form-error-message">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">Något gick fel</p>
            <p className="text-red-600 text-sm">Försök igen eller ring oss på 08-25 46 04.</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 relative z-20">
        {/* Name */}
        <div>
          <Label htmlFor="name" className="form-label">
            Namn *
          </Label>
          <Input
            id="name"
            type="text"
            placeholder="Ditt namn"
            className={`form-input ${errors.name ? 'border-red-500' : ''}`}
            {...register('name')}
            data-testid="form-name-input"
          />
          {errors.name && (
            <p className="text-red-600 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <Label htmlFor="phone" className="form-label">
            Telefon *
          </Label>
          <Input
            id="phone"
            type="tel"
            placeholder="070-123 45 67"
            className={`form-input ${errors.phone ? 'border-red-500' : ''}`}
            {...register('phone')}
            data-testid="form-phone-input"
          />
          {errors.phone && (
            <p className="text-red-600 text-sm mt-1">{errors.phone.message}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <Label htmlFor="email" className="form-label">
            E-post *
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="din@email.se"
            className={`form-input ${errors.email ? 'border-red-500' : ''}`}
            {...register('email')}
            data-testid="form-email-input"
          />
          {errors.email && (
            <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>
          )}
        </div>

        {/* Property Type */}
        <div>
          <Label htmlFor="property_type" className="form-label">
            Typ av fastighet *
          </Label>
          <Select
            onValueChange={(value) => setValue('property_type', value)}
          >
            <SelectTrigger
              className={`form-input ${errors.property_type ? 'border-red-500' : ''}`}
              data-testid="form-property-type-select"
            >
              <SelectValue placeholder="Välj fastighetstyp" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="villa">Villa</SelectItem>
              <SelectItem value="radhus">Radhus</SelectItem>
              <SelectItem value="brf">BRF / Lägenhet</SelectItem>
              <SelectItem value="fastighet">Fastighet / Flerfamiljshus</SelectItem>
              <SelectItem value="fritidshus">Fritidshus</SelectItem>
              <SelectItem value="annat">Annat</SelectItem>
            </SelectContent>
          </Select>
          {errors.property_type && (
            <p className="text-red-600 text-sm mt-1">{errors.property_type.message}</p>
          )}
        </div>

        {/* Message */}
        <div>
          <Label htmlFor="message" className="form-label">
            Meddelande (valfritt)
          </Label>
          <Textarea
            id="message"
            placeholder="Beskriv gärna ditt projekt, antal fönster, skick etc."
            className="form-input min-h-[120px] resize-none"
            {...register('message')}
            data-testid="form-message-textarea"
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-cta w-full text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="form-submit-button"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Skickar...
            </>
          ) : (
            <>
              Skicka offertförfrågan
              <Send className="w-5 h-5" />
            </>
          )}
        </button>
      </form>

      {/* Trust text */}
      <p className="text-center text-sm text-[#94A3B8] mt-6">
        Vi behandlar dina uppgifter enligt GDPR. Du blir kontaktad av Vi Målar.
      </p>
    </div>
  );
};

export default LeadForm;
